import classNames from 'classnames';

import Error from '~/shared/components/error';
import getRowItems from '~/shared/components/items-navigator/helpers/get-row-items';
import getTileItems from '~/shared/components/items-navigator/helpers/get-tile-items';
import useItemsNavigator from '~/shared/components/items-navigator/use-items-navigator';
import Loading from '~/shared/components/loading';
import Pagination from '~/shared/components/pagination';
import SortBar from '~/shared/components/sort-bar';
import { LIST_VIEW_TYPE } from '~/shared/constants';
import type { ItemsPaginationType } from '~/shared/types/general-type';

import '~/shared/components/items-navigator/items-navigator.scss';

type ItemsNavigatorPropsType = {
  withLoadMore: boolean;
  withPagination: boolean;
  isMobile: boolean;
  withSortBar: boolean;
  isContainItemImage?: boolean;
  loading?: boolean;
  itemType: string;
  viewType?: string;
  pageTagsToGo?: string;
  pageToGo: string;
  data: ItemsPaginationType;
  fetchMore?: ({ nextPage, sort }: { nextPage: number; sort: string }) => void;
  t: (key: string, options: { ns: string[] }) => string;
};

const ItemsNavigator = ({
  withPagination,
  getTagLink,
  readMoreText,
  withLoadMore,
  showMoreText,
  lng,
  isContainItemImage,
  isMobile,
  uniqueCards,
  withSortBar,
  loading,
  pageToGo,
  itemType,
  viewType,
  error,
  data,
  fetchMore,
  pageTagsToGo = null,
  t,
}: ItemsNavigatorPropsType) => {
  const { listViewType, setSort, sort, onPageClick, setViewType } = useItemsNavigator({ viewType, fetchMore });

  return (
    <section className="items-navigator">
      {loading && <Loading />}
      {error && !loading && <Error />}
      {!loading && !error && (
        <>
          {withSortBar && (
            <SortBar viewType={listViewType} setSort={setSort} sort={sort} setViewType={setViewType} t={t} />
          )}
          <div
            itemScope
            itemType="http://schema.org/Blog"
            className={classNames('items-navigator__content', listViewType !== LIST_VIEW_TYPE.ROW ? 'row' : 'column')}
          >
            {data?.items?.length === 0 && <div className="items-navigator__no-data">{t('text.empty-data')}</div>}

            {listViewType === LIST_VIEW_TYPE.TILE
              ? getTileItems({
                  items: data?.items,
                  pageToGo,
                  lng,
                  uniqueCards,
                  getTagLink,
                  readMoreText,
                  isContainItemImage,
                  pageTagsToGo,
                  t,
                  isMobile,
                  itemType,
                })
              : listViewType === LIST_VIEW_TYPE.ROW
                ? getRowItems({
                    items: data?.items,
                    pageToGo,
                    readMoreText,
                    lng,
                    pageTagsToGo,
                    t,
                    isMobile,
                    itemType,
                  })
                : null}
            {withPagination && (
              <Pagination
                marginPagesDisplayed={2}
                pageRangeDisplayed={5}
                previousLabel={t('text.prev')}
                nextLabel={t('text.next')}
                subContainerClassName="pages pagination"
                breakLabel="..."
                containerClassName="pagination"
                breakClassName="break-me"
                activeClassName="active"
                pageInfo={data?.pageInfo}
                handlePageClick={onPageClick}
              />
            )}
            {withLoadMore && data?.pageInfo?.hasNextPage && (
              <div className="items-navigator__load-more">
                <button
                  className="items-navigator__load-more-button"
                  onClick={() => {
                    onPageClick({ selected: data?.pageInfo?.currentPage + 1 });
                  }}
                >
                  {showMoreText ?? t('text.load-more')}
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );
};

export default ItemsNavigator;
